import styled, { css } from 'styled-components';

import {
  isLightTheme,
  lavenderBorderFor,
  pillBgFor,
  pillBorderFor,
  primaryEmphasisFor,
} from './portPillTheme';

/**
 * Pill capsule wrapping the React Flow {@code <Handle>} for each gate
 * branch output. Visually identical to {@code EdgePillOut} from
 * {@code PortHandles.styled}.
 *
 * {@code right: -35px} compensates for the BranchSlot's 18px horizontal
 * margin so the pill center still lands on the node card's outer edge
 * (design: {@code calc(-1 * (--space-4 + 6px) - 17px)}).
 */
export const BranchHandlePill = styled.div`
  position: absolute;
  right: -35px;
  top: 50%;
  margin-top: -19px;
  width: 22px;
  height: 38px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) =>
    isLightTheme(theme)
      ? (theme.colors?.onPrimaryContainer ?? '#0A006B')
      : 'color-mix(in oklch, white 70%, transparent)'};
  background: ${({ theme }) => pillBgFor(theme)};
  border: 1px solid ${({ theme }) => pillBorderFor(theme)};
  box-shadow: 0 2px 6px rgb(0 0 0 / 0.25);
  cursor: crosshair;
  padding: 0;
  z-index: 3;
  transition:
    transform 120ms ease,
    background 120ms ease,
    border-color 120ms ease,
    box-shadow 120ms ease,
    color 120ms ease;

  &::before {
    content: '';
    position: absolute;
    inset: -10px -14px;
    border-radius: 999px;
    background: transparent;
  }

  &:hover {
    transform: scale(1.08);
    background: ${({ theme }) => primaryEmphasisFor(theme)};
    border-color: ${({ theme }) => lavenderBorderFor(theme)};
    color: ${({ theme }) => theme.colors?.onPrimaryEmphasis ?? '#1E1A8A'};
  }

  & svg {
    display: block;
    margin-left: 1px;
    pointer-events: none;
  }

  & .react-flow__handle {
    position: absolute;
    inset: 0;
    width: auto !important;
    height: auto !important;
    border-radius: 999px;
    background: transparent !important;
    border: 0 !important;
    transform: none !important;
    pointer-events: auto;
  }
`;

interface BranchSlotProps {
  /**
   * True when the slot is wired up to call back into the editor (i.e.
   * the gate is currently waiting on this node and the click reopens
   * the decision modal). Drives cursor / hover / focus affordances so
   * the slot reads as a real button when interactive and as a static
   * label otherwise. Always false in edit/draft mode.
   */
  $interactive?: boolean;
}

/**
 * Pill-shaped decision button inside the gate's branch column. Matches
 * the design's {@code .ns-edit--gate__decision.is-clickable}: inset by
 * 18px from card edges, 1.5px tone-colored border, 999px radius.
 *
 * Tone-based border colouring uses {@code data-tone} to avoid a
 * transient prop. The {@code $interactive} transient prop gates the
 * cursor / hover / focus affordances so the slot looks and behaves
 * like a real control only when clicking actually does something.
 */
export const BranchSlot = styled.div<BranchSlotProps>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 18px;
  padding: 7px 14px;
  background: transparent;
  border: 1.5px solid
    ${({ theme: { colors = {} } }) => colors.outlineVariant ?? '#25263A'};
  border-radius: 999px;
  cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};
  transition:
    border-color 140ms ease,
    transform 80ms ease,
    box-shadow 140ms ease;

  &[data-tone='approved'] {
    border-color: color-mix(
      in srgb,
      ${({ theme: { colors = {} } }) => colors.success ?? '#8CD88C'} 32%,
      transparent
    );
  }
  &[data-tone='rejected'] {
    border-color: color-mix(
      in srgb,
      ${({ theme: { colors = {} } }) => colors.error ?? '#FFB4AB'} 32%,
      transparent
    );
  }

  ${({ $interactive, theme: { colors = {} } }) =>
    $interactive
      ? css`
          &[data-tone='approved']:hover {
            border-color: color-mix(
              in srgb,
              ${colors.success ?? '#8CD88C'} 60%,
              transparent
            );
            box-shadow: 0 0 0 1px
              color-mix(
                in srgb,
                ${colors.success ?? '#8CD88C'} 25%,
                transparent
              );
          }
          &[data-tone='rejected']:hover {
            border-color: color-mix(
              in srgb,
              ${colors.error ?? '#FFB4AB'} 55%,
              transparent
            );
            box-shadow: 0 0 0 1px
              color-mix(
                in srgb,
                ${colors.error ?? '#FFB4AB'} 22%,
                transparent
              );
          }

          &:active {
            transform: translateY(1px);
          }

          &:focus-visible {
            outline: 2px solid ${colors.primary ?? '#C1C1FF'};
            outline-offset: 2px;
          }
        `
      : ''}

  /* Node-level state overrides for the handle pill */
  [data-state='selected'] &,
  [data-hovered='true'] & {
    ${BranchHandlePill} {
      background: ${({ theme }) => primaryEmphasisFor(theme)};
      border-color: ${({ theme }) => lavenderBorderFor(theme)};
      color: ${({ theme }) => theme.colors?.onPrimaryEmphasis ?? '#1E1A8A'};
    }
  }

  [data-state='runDone'] & ${BranchHandlePill} {
    background: ${({ theme: { colors = {} } }) => colors.successFixed ?? '#4ade80'};
    border-color: color-mix(
      in oklch,
      ${({ theme: { colors = {} } }) => colors.successFixed ?? '#4ade80'} 80%,
      white 15%
    );
    color: ${({ theme: { colors = {} } }) => colors.onSuccess ?? '#002106'};
    box-shadow:
      inset 0 1px 0 0 color-mix(in oklch, white 25%, transparent),
      0 2px 4px rgb(0 0 0 / 0.32),
      0 0 8px
        color-mix(
          in oklch,
          ${({ theme: { colors = {} } }) => colors.successFixed ?? '#4ade80'} 45%,
          transparent
        );
  }
`;

interface BranchIconWrapProps {
  $tone: 'approved' | 'rejected';
}

/**
 * 22×22 icon container — green for approved, red for rejected.
 * Matches {@code .ns-edit--gate__icon} + tone modifiers.
 */
export const BranchIconWrap = styled.span<BranchIconWrapProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  color: ${({ $tone }) => ($tone === 'approved' ? '#2BA85A' : '#D24A4A')};

  & svg {
    width: 20px;
    height: 20px;
  }
`;

/**
 * Branch label — 13px / 500 weight, inherits the card's text color.
 * Matches {@code .ns-edit--gate__label} + the
 * {@code font: 500 13px/1.35} from {@code .is-clickable}.
 */
export const BranchLabelText = styled.span`
  flex: 1;
  font-weight: 500;
  font-size: 13px;
  line-height: 1.35;
  color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
`;
